import _ from 'lodash';
import React from 'react';
import * as ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { goTo, handleAlert } from '../../actions';
import styled from 'styled-components';
import { SETTINGS_PAGE, DARK_THEME } from '../../constants';
import MicrophoneIcon from './MicrophoneIcon';
import SubtitlesStyleControls from '../../containers/SubtitlesStyleControls';
import audioClient from './audioClient';
import speechClient from './speechClient';
import cssTheme from '../../theme';
import { findDefaultInput, findDefaultOutput } from './utils';
import {
  Button,
  ButtonGroup,
  Popover,
  Menu,
  MenuItem,
  Position,
  ProgressBar,
  Intent,
  Dialog
} from '@blueprintjs/core';
import alertTypes from '../../constants/alertTypes';

// Restart stream if volume is low enough
// OR if it has been too long.
const LOW_VOLUME = 0.01;
const MIN_BREAK_TIME = 45000;
const MAX_BREAK_TIME = 45000;
const EXTRA_CONTROLS_WIDTH_CUTOFF = '800px';
const RETRY_TIMEOUT = 3000;
const VOLUME_HISTORY_SIZE = 20;

// Refresh button that has a short-lived spinner for effect.
class RefreshButton extends React.Component {

  state = {loading: false};

  onClick = () => {
    this.setState({loading: true});
    this.props.onClick();
    setTimeout(() => this.setState({loading: false}), 500);
  };

  render() {
    const {loading} = this.state;
    return (
      <Button
        icon="refresh"
        loading={loading}
        onClick={this.onClick}
      />
    )
  }
}

// Styling
const MainContainer = styled.div`
  -webkit-app-region: drag;
  display: flex;
  width: 100%;
  height: 100vh;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const Controls = styled.div`
  margin: 2px 0;
  width: 100%;
  min-height: 40px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

const Control = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;  
  @media (max-width: ${EXTRA_CONTROLS_WIDTH_CUTOFF}) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    flex-wrap: wrap;  

  }
`;

const Container = styled.div`
  margin: 10px;
  @media (max-width: ${EXTRA_CONTROLS_WIDTH_CUTOFF}) {
    margin: 10px 0;
  }
`;

const BottomContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;


const CardsContainer = styled.div`
  padding: 0 10px 10px 10px;
  overflow-y: scroll;
  font-size: ${props => props.fontSize}px;
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
`;

const Card = styled.div`
    margin: 2px 5px;
    width: 100%;
  `;

const ExtraControls = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  flex-wrap: wrap;
  @media (max-width: ${EXTRA_CONTROLS_WIDTH_CUTOFF}) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
`;


const Volume = styled.div`
  margin: 10px;
  width: 100px;
`;

const Translation = styled.blockquote`
  margin-top: 2px;
  font-style: italic;
  color: ${props => props.dark ? cssTheme.gray4 : cssTheme.gray3};
`;


class Subtitles extends React.Component {

  streamStartDate;

  volumeHistory = [];

  state = {
    showControls: false,
    // cards =
    // {
    //    <cardId>: {
    //        transcription: <str> ,
    //        translation: <str>
    //    }
    // }
    cards: {},
    volume: 0,
    playerEnabled: false,
    speechClientConnected: false,
    speechServerStarted: false,
    // Audio inputs and outputs + selected input / output.
    // Populated later, once the audio client initializes.
    inputs: [],
    outputs: [],
    input: null,
    output: null
  };


  componentDidMount() {
    this.streamStartDate = Date.now();
    this.connectSpeechClient();
    this.initializeAudioClient();
  }

  componentWillUpdate(nextProps, nextState) {
    // [NOTE] Auto scrolling based on tutorial:
    // https://www.pubnub.com/tutorials/react/
    // chat-message-history-and-infinite-scroll/#infinite-scrolling
    //
    // Keep track of:
    // 0. whether the cards have changed (this.cardsChanged)
    // 1. whether we should scroll at the bottom (this.scrollAtBottom)
    // 2. bottom card so that it can be scrolled into view (this.bottomCard)
    // const prevNumCards = Object.keys(nextState.cards).length;
    // const numCards = Object.keys(this.state.cards).length;
    // this.cardsChanged = prevNumCards !== numCards;
    this.cardsChanged = this.state.cards !== nextState.cards;
    if (this.cardsChanged) {
      const {cardsContainerRef} = this;
      const scrollPos = cardsContainerRef.scrollTop;
      const scrollBottom = (cardsContainerRef.scrollHeight - cardsContainerRef.clientHeight);
      this.scrollAtBottom = (scrollBottom <= 0) || (scrollPos === scrollBottom);
      if (!this.scrollAtBottom) {
        const numCards = cardsContainerRef.childNodes.length;
        this.bottomCard = numCards === 0 ? null : cardsContainerRef.childNodes[-1];
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // [NOTE] Auto scrolling based on tutorial:
    // https://www.pubnub.com/tutorials/react/
    // chat-message-history-and-infinite-scroll/#infinite-scrolling
    //
    // The behavior is such that if the cards changed and
    // the user has scrolled to the bottom, then the app
    // will auto-scroll to the new bottom (since new data was rendered).
    // Otherwise, if the app will scroll, topMessage it into view.
    if (this.cardsChanged) {
      if (this.scrollAtBottom) {
        this.scrollToBottom();
      }
      if (this.bottomCard) {
        ReactDOM.findDOMNode(this.bottomCard).scrollIntoView();
      }
    }

    // Sync devices, input and output
    const {input, output} = this.state;
    const prevInput = prevState.input;
    const prevOutput = prevState.output;
    const inputChange = _.get(input, 'deviceId') !== _.get(prevInput, 'deviceId');
    const outputChange = _.get(output, 'deviceId') !== _.get(prevOutput, 'deviceId');

    if (inputChange || outputChange) {
      audioClient.setInput(input);
      audioClient.setOutput(output);
      audioClient.createPipeline()
        .then(() => {
          console.log('Pipeline created.');
        })
        .catch(error => {
          this.props.handleAlert({
            type: alertTypes.ERROR,
            message: 'Audio Pipeline Error: ' + error
          });
        });
    }
  }


  initializeAudioClient = () => {

    const onAudio = ({volume, audioBuffer}) => {

      // Update volume history
      if (this.volumeHistory.length > VOLUME_HISTORY_SIZE) {
        this.volumeHistory.shift();
      }
      this.volumeHistory.push(volume);

      // Get average volume
      const sum = this.volumeHistory.reduce((a, b) => a + b, 0);
      const avgVolume = sum / this.volumeHistory.length;

      if (avgVolume > LOW_VOLUME) {
        if (!this.state.speechServerStarted) speechClient.start();

        // Update volume indicator
        this.setState({volume});

        // Restart stream if volume is low enough AND it's been long enough
        // OR if it has been too long.
        const minBreakTime = this.streamStartDate + MIN_BREAK_TIME;
        const maxBreakTime = this.streamStartDate + MAX_BREAK_TIME;
        const restart = (
            (volume < LOW_VOLUME) && (Date.now() > minBreakTime)
          ) || (Date.now() > maxBreakTime);

        if (restart) {
          this.streamStartDate = Date.now();
          speechClient.restart();
        }

        // Send audio data to server for processing
        speechClient.send(audioBuffer);
      } else {
        if (this.state.speechServerStarted) speechClient.stop();
      }
    };

    audioClient.initialize(onAudio)
      .then(({input, output, inputs, outputs, playerEnabled}) => {
        this.setState({input, output, inputs, outputs, playerEnabled});
      })
      .catch(error => {
        this.props.handleAlert({
          type: alertTypes.ERROR,
          message: 'Audio Pipeline Initialization Error: ' + error
        });
      });
  };


  handleRefreshButtonClick = () => {
    audioClient.discoverInputsAndOutputs()
      .then(({inputs, outputs}) => {
        let {input, output} = this.state;
        const validInput = _.find(inputs, {label: input.label});
        const validOutput = _.find(outputs, {label: output.label});
        if (!validInput) input = findDefaultInput(inputs);
        if (!validOutput) output = findDefaultOutput(outputs).output;
        this.setState({inputs, outputs, input, output});
      })
      .catch(error => {
        this.props.handleAlert({
          type: alertTypes.ERROR,
          message: 'Unable to read list of audio devices: ' + error
        });
      });
  };


  connectSpeechClient = () => {

    const onOpen = () => this.setState({speechClientConnected: true});

    const onClose = () => {
      setTimeout(() => this.connectSpeechClient(), RETRY_TIMEOUT);
      this.setState({
        speechClientConnected: false,
        speechServerStarted: false
      });
    };

    const onData = ({card}) => {
      const cardId = card.id;
      const {transcription, translation} = card;

      const cards = Object.assign({}, this.state.cards);
      cards[cardId] = cards[cardId] || {};

      if (transcription) cards[cardId].transcription = transcription;
      if (translation) cards[cardId].translation = translation;

      this.setState({cards});
    };

    const onStatus = (speechServerStarted) => {
      console.log('SpeechServer started: ' + speechServerStarted);
      this.setState({speechServerStarted});
    };

    const onError = (error) => {
      this.props.handleAlert({
        type: alertTypes.ERROR,
        message: 'Speech Server Error: ' + error
      });
    };

    speechClient.connect(onOpen, onClose, onData, onStatus, onError);
  };

  handleNodeSelect = (nodes, stateKey) => {
    return e => {
      const deviceLabel = e.target.textContent;
      const selectedDeviceKey = _.findKey(nodes, {label: deviceLabel}, null);
      const selectedNode = _.get(nodes, [selectedDeviceKey], null);
      const stateUpdate = {volume: 0};
      stateUpdate[stateKey] = selectedNode;
      this.setState(stateUpdate);
    };
  };

  handleAudioInputSelect = e => {
    this.handleNodeSelect(this.state.inputs, 'input')(e);
  };

  handleAudioOutputSelect = e => {
    this.handleNodeSelect(this.state.outputs, 'output')(e);
  };

  renderDropdown = (nodes, selectedNode, selectionHandler) => {

    const menuItems = [];
    nodes.forEach(node => {
      menuItems.push(<MenuItem
        onClick={selectionHandler}
        key={nodes.indexOf(node)}
        text={node.label}
      />)
    });

    const dropdownMenu = <Menu>{menuItems}</Menu>;

    const dropdownButton = <Button
      className="button-align-left"
      rightIcon="caret-down"
      text={selectedNode ? selectedNode.label : 'Select Device'}
    />;

    return [
      <RefreshButton
        onClick={this.handleRefreshButtonClick}
        key="refresh"
      />,
      <Popover
        key="dropdown"
        minimal
        target={dropdownButton}
        content={dropdownMenu}
        position={Position.BOTTOM_LEFT}
      />
    ];
  };

  toggleOutputStartStop = () => {
    if (this.state.playerEnabled) {
      audioClient.stopPlayer();
      this.setState({playerEnabled: false});
    } else {
      audioClient.startPlayer();
      this.setState({playerEnabled: true});
    }
  };

  toggleControls = () => {
    if (this.state.showControls) {
      this.setState({showControls: false});
    } else {
      this.setState({showControls: true})
    }
  };

  openSettings = () => this.props.goTo(SETTINGS_PAGE);

  onScroll = () => {
    const {cardsContainerRef, props} = this;
    const scrollTop = cardsContainerRef.scrollTop;
    if (scrollTop === 0) {
      // props.fetchHistory();
    }
  };

  scrollToBottom = () => {
    const {cardsContainerRef} = this;
    if (cardsContainerRef) {
      const scrollHeight = cardsContainerRef.scrollHeight;
      const height = cardsContainerRef.clientHeight;
      const maxScrollTop = scrollHeight - height;
      ReactDOM.findDOMNode(cardsContainerRef).scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
    }
  };

  render() {
    const {
      inputs,
      input,
      outputs,
      output,
      playerEnabled,
      volume,
      speechClientConnected,
      speechServerStarted,
      showControls,
      cards
    } = this.state;


    const {fontSize, theme, translation} = this.props;

    // eslint-disable-next-line
    const inputDropdown = this.renderDropdown(
      inputs, input, this.handleAudioInputSelect);
    const outputDropdown = this.renderDropdown(
      outputs, output, this.handleAudioOutputSelect);

    const statusIndicator = speechClientConnected ? (
        speechServerStarted ? (
            <div className="pt-tag pt-large pt-intent-danger">
              Using Streaming Credits
            </div>
          ) : (
            <div className="pt-tag pt-large pt-intent-success">
              Ready
            </div>
          )
      ) : (
        <div className="pt-tag pt-large pt-intent-danger">
          Server Disconnected
        </div>
      );

    const extraControls = (
      <ExtraControls>
        {/* Input Info */}
        <Control>
          <div className="pt-tag pt-large">Input Audio</div>
          <Container>
            <ButtonGroup>
              {inputDropdown}
            </ButtonGroup>
          </Container>
          <Container className="flex-center-row">
            <MicrophoneIcon/>
            <Volume>
              <ProgressBar
                intent={Intent.SUCCESS}
                value={volume}
                className="pt-no-stripes"
              />
            </Volume>
          </Container>
        </Control>
        {/* Output Controls */}
        <Control>
          <div className="pt-tag pt-large">Output Audio</div>
          <Container>
            <ButtonGroup>
              {outputDropdown}
              <Button
                icon={playerEnabled ? "volume-off" : "volume-up"}
                text={playerEnabled ? "Stop" : "Play"}
                onClick={this.toggleOutputStartStop}
              />
            </ButtonGroup>
          </Container>
        </Control>
        {/* Status indicators and caption control */}
        <Control>
          <Button
            className="pt-intent-primary"
            icon="share"
            text="More Settings"
            onClick={this.openSettings}
          />
        </Control>
      </ExtraControls>
    );

    const extraControlsButton = (
      <Button
        className="pt-minimal pt-large"
        icon="cog"
        onClick={this.toggleControls}
      />
    );

    const cardComponents = [];
    for (let cardId of Object.keys(cards)) {
      const showTranslation = translation.enabled && cards[cardId].translation;
      const translationBlock = (
        <Translation dark={theme === DARK_THEME}>
          {this.state.cards[cardId].translation}
        </Translation>
      );
      let cardComponent = (
        <Card key={cardId}>
          {this.state.cards[cardId].transcription}
          {showTranslation ? translationBlock : null}
        </Card>
      );
      cardComponents.push(cardComponent);
    }

    const content = (
      <CardsContainer
        innerRef={ref => this.cardsContainerRef = ref}
        onScroll={this.onScroll}
        fontSize={fontSize}
      >
        {cardComponents}
      </CardsContainer>
    );

    return (
      <MainContainer className={`pt-card ${theme} no-border-radius`}
                     style={{padding: 0}}>
        <Controls>
          <SubtitlesStyleControls/>
          {extraControlsButton}
        </Controls>
        <Dialog
          icon="pulse"
          canOutsideClickClose={false}
          isOpen={showControls}
          onClose={this.toggleControls}
          title="Audio Settings"
          style={{width: '100%', paddingBottom: 0, margin: '0 40px'}}
        >
          <div className="pt-dialog-body flex-center-column">
            {extraControls}
          </div>
        </Dialog>
        {content}
      </MainContainer>
    );
  }
}

function mapStateToProps(state) {
  return {
    theme: state.theme,
    fontSize: state.fontSize,
    translation: state.translation
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    goTo,
    handleAlert
  }, dispatch)
}

Subtitles = connect(mapStateToProps, mapDispatchToProps)(Subtitles);

export default Subtitles;



// WEBPACK FOOTER //
// ./src/containers/Subtitles/index.js